import { useLayoutEffect } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { App } from 'src/App/App';

import { PermissionRoute } from './PermissionRoute';

import { getPath, TertiaryLayoutSkeleton } from '@speeki/client-admin';
import { FeatureFlag } from '@speeki/feature-flags';
import { resolveEnv } from '@speeki/frontend-utils';

import { useUserModules } from '@utils/hooks/useUserModules';

import { AcademyPlaceholder } from '@pages/AcademyPlaceholder/AcademyPlaceholder';
import { AssessmentPlaceholder } from '@pages/AssessmentPlaceholder/AssessmentPlaceholder';
import { CarbonLensPlaceholder } from '@pages/CarbonLensPlaceholder/CarbonLensPlaceholder';
import { CertificatesPlaceholder } from '@pages/CertificatesPlaceholder/CertificatesPlaceholder';
import { CommsPlaceholder } from '@pages/CommsPlaceholder/CommsPlaceholder';
import { DashboardPlaceholder } from '@pages/DashboardPlaceholder/DashboardPlaceholder';
import { DocumentsPlaceholder } from '@pages/DocumentsPlaceholder/DocumentsPlaceholder';
import { LibraryPlaceholder } from '@pages/LibraryPlaceholder/LibraryPlaceholder';
import { TrainingPlaceholder } from '@pages/TrainingPlaceholder/TrainingPlaceholder';
import * as Sentry from '@sentry/react';

export const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const DashboardRedirect = () => {
    const {
        hasEsgAccess,
        hasFrameworksAccess,
        hasIncidentsAccess,
        userLoading,
    } = useUserModules();

    useLayoutEffect(() => {
        if (userLoading) return;

        // Priority: frameworks → programmes → incidents dashboard →
        // stay (show dashboard placeholder)
        const redirectPath = hasFrameworksAccess
            ? '/frameworks'
            : hasEsgAccess
              ? '/programmes'
              : hasIncidentsAccess
                ? '/incidents/dashboard'
                : null;

        if (!redirectPath) return;

        window.location.replace(`${window.location.origin}${redirectPath}`);
    }, [hasEsgAccess, hasFrameworksAccess, hasIncidentsAccess, userLoading]);

    if (userLoading) return <TertiaryLayoutSkeleton />;

    return <DashboardPlaceholder />;
};

export const routes: RouteObject[] = [
    {
        children: [
            {
                element: <DashboardRedirect />,
                path: '/dashboard',
            },
            {
                element: <AssessmentPlaceholder />,
                path: '/tools/assessment',
            },
            {
                element: <CertificatesPlaceholder />,
                path: '/tools/assurance',
            },
            {
                element: <DocumentsPlaceholder />,
                path: '/tools/documents',
            },
            {
                element: <AcademyPlaceholder />,
                path: '/tools/academy',
            },
            {
                element: <CommsPlaceholder />,
                path: '/tools/comms',
            },
            {
                element: <TrainingPlaceholder />,
                path: '/tools/training',
            },

            {
                element: <LibraryPlaceholder />,
                path: '/tools/library',
            },
            {
                element: (
                    <FeatureFlag
                        featured={
                            <PermissionRoute
                                permissions={['CARBON_LENS_MODULE']}
                                redirectTo={{ path: '/dashboard' }}
                            >
                                <CarbonLensPlaceholder />
                            </PermissionRoute>
                        }
                        flag={'client_admin-carbon_lens-20250401'}
                        old={
                            <Navigate
                                replace
                                to={getPath({ path: '/tools/carbon-lens' })}
                            />
                        }
                    />
                ),
                path: '/carbon-lens',
            },
            {
                element: (
                    <FeatureFlag
                        featured={
                            <Navigate
                                replace
                                to={getPath({ path: '/carbon-lens' })}
                            />
                        }
                        flag={'client_admin-carbon_lens-20250401'}
                        old={<CarbonLensPlaceholder />}
                    />
                ),
                path: '/tools/carbon-lens',
            },
            {
                element: (
                    <Navigate replace to={getPath({ path: '/dashboard' })} />
                ),
                path: '/',
            },
            {
                element: (
                    <Navigate replace to={getPath({ path: '/dashboard' })} />
                ),
                path: '*',
            },
        ],
        element: <App />,
        path: '/',
    },
];

const publicUrl = resolveEnv('PUBLIC_URL');

export const router = sentryCreateBrowserRouter(routes, {
    basename: publicUrl.endsWith('/') ? publicUrl : `${publicUrl}/`,
});
